/** Number formatting for the FormalRV Shor leaderboard. */

const sig3 = (x: number) => Number(x.toPrecision(3))

/** 17,737,805 -> "17.7M", 400,371 -> "400k", 93,600 -> "93.6k". */
export function fmtQubits(n: number): string {
  if (n >= 1e6) return `${sig3(n / 1e6)}M`
  if (n >= 1e3) return `${sig3(n / 1e3)}k`
  return String(n)
}

/** Days -> "5.05 h", "14.2 d", "10.7 yr", "3,169 yr". */
export function fmtDuration(days: number): string {
  if (days < 1) return `${sig3(days * 24)} h`
  if (days < 730) return `${sig3(days)} d`
  const years = days / 365.25
  if (years < 100) return `${sig3(years)} yr`
  return `${Math.round(years).toLocaleString()} yr`
}

/** Ratio of checked to claimed, e.g. "68×" or "0.89×". */
export function fmtRatio(checked: number, claimed: number): string {
  const r = checked / claimed
  if (r >= 100) return `${Math.round(r).toLocaleString()}×`
  return `${sig3(r)}×`
}

/** Exact integer, possibly shipped as a string because it exceeds 2^53. */
export function fmtExact(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string' && /^-?\d+$/.test(v)) return BigInt(v).toLocaleString()
  if (typeof v === 'number') return v.toLocaleString()
  return String(v)
}

export function fmtSci(x: number, digits = 3): string {
  if (x === 0) return '0'
  const exp = Math.floor(Math.log10(Math.abs(x)))
  const mant = Number((x / 10 ** exp).toPrecision(digits))
  if (exp >= -2 && exp <= 4) return Number(x.toPrecision(digits)).toLocaleString()
  return `${mant}·10${toSuperscript(exp)}`
}

function toSuperscript(n: number): string {
  const map: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' }
  return String(n).split('').map(c => map[c] ?? c).join('')
}

/** "measured-services" -> "measured services"; null -> "default". */
export function fmtMode(mode: string | null | undefined, requireSpecs?: boolean | null): string {
  if (!mode || mode === 'default') return requireSpecs ? 'default · --require-specs' : 'default'
  return mode.replace(/-/g, ' ')
}

export const isDefaultMode = (mode: string | null | undefined, requireSpecs?: boolean | null) =>
  (!mode || mode === 'default') && !!requireSpecs
